import { useTranslation } from "react-i18next";
import { Flex } from "../../../components/Flex";
import { FontSize, Text } from "../../../components/Text";
import { Info } from "../../../components/Info";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { permissionActions } from "../../../redux/permission/permission.slice";
import { moduleActions } from "../../../redux/module/module.slice";
import { moduleSliceSelectors } from "../../../redux/module/module.selector";
import { Button } from "../../../components/Button";
import { WarningModal } from "../../../components/WarningModal";
import { AtiraToast } from "../../../utils/AtiraToast";
import { ModuleDefinition } from "../../../model/module/types/module-definition/ModuleDefinition";
import { ModuleDefinitionCreateEditDrawer } from "../../modules/module-definition/components/ModuleDefinitionCreateEditDrawer";
import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons/faPenToSquare";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { thresholdActions } from "../../../redux/threshold/threshold.slice";
import { thresholdSliceSelectors } from "../../../redux/threshold/threshold.selector";
import { useNavigate } from "react-router-dom";
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons/faLayerGroup";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import { TextWithTruncated } from "../../../components/TextWithTruncated";
import { Lengths } from "../../../model/shared/enum/Lengths.enum";
import { Rounded } from "../../../theme/Rounded";
import { Tooltip } from "../../../utils/local-antd";
import { ModuleDefinitionSettingsDrawer } from "../../modules/module-definition/components/settings/ModuleDefinitionSettingsDrawer";
import { faGear } from "@fortawesome/free-solid-svg-icons/faGear";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { faTimeline } from "@fortawesome/free-solid-svg-icons/faTimeline";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { getFontAwesomeIcon } from "../../../utils/getFontAwesomeIcon";
import { faCube } from "@fortawesome/free-solid-svg-icons/faCube";
import { SettingsWrapper } from "../components/SettingsWrapper";
import { useDrawersContext } from "../../tasks/drawers-context";
import { TestIDs } from "../../../tests/types/testIds";

const ModuleCard = styled(Flex)`
  flex-direction: column;
  padding: ${Spacing.m};
  border-radius: ${Rounded.md};
  background: ${({ theme }) => theme.main};
  position: relative;
  overflow: hidden;
`;

const ModuleKind = styled(Text)`
  font-size: ${FontSize.s};
  color: ${({ theme }) => theme.main};
  background-color: ${({ theme }) => theme.sub};
  padding: ${Spacing.xs} ${Spacing.s};
  border-end-start-radius: ${Rounded.md};
  position: absolute;
  top: 0;
  inset-inline-end: 0;
  min-width: 6rem;
  text-align: center;
`;

const Name = styled(TextWithTruncated)`
  font-weight: 700;
  font-size: calc(${FontSize.s} + 0.15rem);
  color: ${({ theme }) => theme.white};
`;

const Description = styled(TextWithTruncated)`
  font-size: ${FontSize.s};
  color: ${({ theme }) => theme.sub};
  margin-top: ${Spacing.xs};
`;

const ActionsRow = styled(Flex)`
  margin-top: ${Spacing.m};
  justify-content: flex-end;
  gap: ${Spacing.s};
`;

const IconActionButton = styled(Button)<{ isDanger?: boolean }>`
  background: transparent;
  padding: ${Spacing.s};
  margin: 0;
  color: ${({ theme }) => theme.sub};

  &:hover {
    background: ${({ theme }) => theme.lightSub};
    color: ${({ theme, isDanger }) => theme[isDanger ? "danger" : "lightMain"]};
  }
`;

export const SettingsModules: React.FC = () => {
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [
    moduleDefinitionCreateEditDrawerVisible,
    setModuleDefinitionCreateEditDrawerVisible,
  ] = useState(false);
  const [
    moduleDefintionSettingsDrawerVisible,
    setModuleDefintionSettingsDrawerVisible,
  ] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { setModuleDefinitionActivityDrawerVisible } = useDrawersContext();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const moduleDefinitons = useAppSelector(
    moduleSliceSelectors.selectAllModuleDefinitions,
  );
  const selectedModuleDefinition = useAppSelector(
    moduleSliceSelectors.selectSelectedModuleDefinition,
  );
  const thresholds = useAppSelector(
    thresholdSliceSelectors.selectThresholdStats,
  );
  const members = useAppSelector(permissionSliceSelectors.selectMembers);

  const canCreateModuleDefinition =
    (thresholds?.consumed?.MODULES_DEFINITIONS || 0) <
    (thresholds?.total?.MODULES_DEFINITIONS || 0);

  const onDelete = (moduleDefinition: ModuleDefinition) => {
    dispatch(moduleActions.setSelectedModuleDefinition(moduleDefinition));
    setDeleteModalVisible(true);
  };

  const onEdit = (moduleDefinition: ModuleDefinition) => {
    dispatch(moduleActions.setSelectedModuleDefinition(moduleDefinition));
    setModuleDefinitionCreateEditDrawerVisible(true);
  };

  const onViewInstances = (moduleDefinition: ModuleDefinition) => {
    navigate(`/modules/${moduleDefinition._id}`);
  };

  const onOpenSettings = (moduleDefinition: ModuleDefinition) => {
    dispatch(moduleActions.setSelectedModuleDefinition(moduleDefinition));
    setModuleDefintionSettingsDrawerVisible(true);
  };

  const onOpenActivity = (moduleDefinition: ModuleDefinition) => {
    dispatch(moduleActions.setSelectedModuleDefinition(moduleDefinition));
    setModuleDefinitionActivityDrawerVisible(true);
  };

  const deleteModule = async () => {
    try {
      setDeleteLoading(true);

      await dispatch(
        moduleActions.deleteModuleDefinition({
          moduleDefinitionId: selectedModuleDefinition?._id!,
          userId,
        }),
      ).unwrap();

      await Promise.all([
        dispatch(moduleActions.getAllModuleDefinitions({ userId })).unwrap(),
        dispatch(thresholdActions.getThresholds({ userId })).unwrap(),
      ]);

      setDeleteModalVisible(false);
      dispatch(moduleActions.setSelectedModuleDefinition(null));

      AtiraToast.success(t("modules.definiton.delete.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    dispatch(moduleActions.getAllModuleDefinitions({ userId }));
    dispatch(permissionActions.getMembers({ userId }));
    dispatch(thresholdActions.getThresholds({ userId }));
  }, [dispatch, userId]);

  return (
    <SettingsWrapper>
      <Text fontWeight={"bold"}>{t("common.modules")}</Text>
      <Text fontSize="s" color="darkerSub">
        {t("modules.definition.getStarted.description-1")}
      </Text>
      <Info info={t("modules.definition.getStarted.description-2")} />

      <Flex flexDirection="column" gap="s" marginTop="s">
        {moduleDefinitons?.length ? (
          moduleDefinitons.map((mod) => {
            return (
              <ModuleCard key={mod._id}>
                <ModuleKind>
                  {t(`modules.defintion.kind.${mod.kind.toLowerCase()}`)}
                </ModuleKind>

                <Flex alignItems="center" gap="m">
                  <Flex alignItems="center" gap="s">
                    <AtiraIcon
                      icon={
                        getFontAwesomeIcon(mod.icon) ||
                        (faCube as IconDefinition)
                      }
                      color="sub"
                    />
                    <Name length={Lengths.NAME}>{mod.name}</Name>
                  </Flex>
                </Flex>

                {mod?.description ? (
                  <Description length={55}>{mod.description}</Description>
                ) : null}

                <ActionsRow>
                  {members?.length ? (
                    <Tooltip title={t("common.settings")}>
                      <IconActionButton
                        icon={faGear}
                        onClick={() => onOpenSettings(mod)}
                        dataTestId={TestIDs.modules.settings.module.permissions(
                          mod.name,
                        )}
                      />
                    </Tooltip>
                  ) : null}

                  <Tooltip title={t("modules.actions.viewInstances")}>
                    <IconActionButton
                      icon={faLayerGroup}
                      onClick={() => onViewInstances(mod)}
                    />
                  </Tooltip>

                  <Tooltip title={t("common.activity")}>
                    <IconActionButton
                      icon={faTimeline}
                      onClick={() => onOpenActivity(mod)}
                    />
                  </Tooltip>

                  <Tooltip title={t("common.edit")}>
                    <IconActionButton
                      icon={faPenToSquare}
                      onClick={() => onEdit(mod)}
                      dataTestId={TestIDs.modules.settings.module.edit(
                        mod.name,
                      )}
                    />
                  </Tooltip>

                  <Tooltip title={t("common.delete")}>
                    <IconActionButton
                      icon={faTrash}
                      onClick={() => onDelete(mod)}
                      isDanger
                    />
                  </Tooltip>
                </ActionsRow>
              </ModuleCard>
            );
          })
        ) : (
          <Text fontSize="s" color="darkerSub" align="center">
            {t("modules.empty.title")}
          </Text>
        )}
      </Flex>

      <Button
        onClick={() => setModuleDefinitionCreateEditDrawerVisible(true)}
        disabled={!canCreateModuleDefinition}
        icon={faPlus}
        dataTestId={TestIDs.modules.settings.button}
      >
        {t("modules.empty.action")}
      </Button>

      <ModuleDefinitionCreateEditDrawer
        open={moduleDefinitionCreateEditDrawerVisible}
        onClose={() => setModuleDefinitionCreateEditDrawerVisible(false)}
      />

      <ModuleDefinitionSettingsDrawer
        open={moduleDefintionSettingsDrawerVisible}
        onClose={() => setModuleDefintionSettingsDrawerVisible(false)}
        moduleDefinition={selectedModuleDefinition}
      />

      <WarningModal
        title={t("modules.definiton.delete.title", {
          name: selectedModuleDefinition?.name,
        })}
        description={t("modules.definiton.delete.description")}
        onConfirm={deleteModule}
        isOpen={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        loading={deleteLoading}
      />
    </SettingsWrapper>
  );
};
